import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import CTAButton from '../components/CTAButton';

interface FAQItem {
  question: string;
  answer: string;
}

function FAQAccordion({ items }: { items: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
          >
            <h3 className="font-semibold text-gray-900">{item.question}</h3>
            <ChevronDown
              className={`w-5 h-5 text-gray-600 transition-transform ${openIndex === index ? 'rotate-180' : ''}`}
            />
          </button>
          {openIndex === index && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <p className="text-gray-700 leading-relaxed">{item.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function FAQPage() {
  const generalFAQ: FAQItem[] = [
    {
      question: 'How do you determine your offer?',
      answer: 'We look at comparable sales in your area, your property\'s condition, any needed repairs, and current market trends. Our goal is to make a fair offer that reflects what we can pay while still providing value to you. After you submit your information, we typically get back to you within 24-48 hours.'
    },
    {
      question: 'Is there any obligation to accept an offer?',
      answer: 'No obligation at all. Getting an offer is completely free. If you don\'t like the offer, you can simply decline, and we won\'t follow up or bother you again. It\'s entirely your choice.'
    },
    {
      question: 'Do I need to repair or clean anything?',
      answer: 'No. We buy homes in as-is condition. Whether your home needs a new roof, plumbing work, or just general cleaning, we can handle it. That\'s the point—you don\'t have to spend money or time on repairs.'
    },
    {
      question: 'Do I have to pay any fees or commissions?',
      answer: 'No fees or commissions. We pay all closing costs. The offer we make to you is what you walk away with (minus any existing liens or mortgages you owe). That\'s it.'
    },
    {
      question: 'How fast can we close?',
      answer: 'We can typically close in as little as 7 days if you\'re in a hurry. But if you need more time, we can work with that too—whether it\'s 30 days, 60 days, or longer. You choose the closing date.'
    },
    {
      question: 'Do I get to choose my closing date?',
      answer: 'Yes, absolutely. Once we agree on terms, you pick when you want to close. We\'ll work around your schedule—whether that\'s next week or two months from now.'
    }
  ];

  const complexFAQ: FAQItem[] = [
    {
      question: 'What if I\'m behind on payments or facing foreclosure?',
      answer: 'We help homeowners in foreclosure situations regularly. We can potentially buy your home and help you avoid foreclosure entirely. The sooner you contact us, the more options you may have. Time is important in these situations, so reach out as soon as possible.'
    },
    {
      question: 'What if the property is inherited?',
      answer: 'Inherited properties are one of the most common situations we help with. We can handle the sale regardless of whether there are multiple heirs or complications with the estate. We work with your timeline and handle the paperwork smoothly.'
    },
    {
      question: 'What if I have tenants in the property?',
      answer: 'We buy rental properties, so tenants are not a problem. We can handle existing tenancies. Just let us know the situation, and we\'ll work with you on the details.'
    },
    {
      question: 'What if the property needs major repairs?',
      answer: 'That\'s actually our specialty. Houses that need new roofs, foundation work, electrical updates, plumbing—we buy them all as-is. You don\'t have to worry about expensive repairs. That\'s why many people choose us.'
    },
    {
      question: 'What if I still owe money on the mortgage?',
      answer: 'No problem. We handle the payoff of your existing mortgage from the sale proceeds. At closing, your lender gets paid, we get our investment, and you get the remaining cash (if any). It\'s all handled smoothly.'
    },
    {
      question: 'What if there\'s a lien on the property?',
      answer: 'We can work with most liens—tax liens, contractor liens, judgment liens. We\'ll discuss the situation and figure out how to handle it. In most cases, liens get paid off at closing.'
    },
    {
      question: 'What if there\'s an HOA or special assessments?',
      answer: 'We account for HOA fees and any pending assessments. Just let us know about them, and we\'ll factor them into our offer.'
    }
  ];

  const processFAQ: FAQItem[] = [
    {
      question: 'What happens after I submit my information?',
      answer: 'We review your property details and reach out to you within 24-48 hours with a preliminary offer. We\'ll explain how we came up with the number and answer any questions. There\'s no pressure to move forward—you can take time to think about it.'
    },
    {
      question: 'Do I have to let you see the property?',
      answer: 'To finalize our offer, we typically like to do a brief walkthrough. But this is scheduled at your convenience and is low-pressure. If you\'re not interested after seeing the initial offer, you can skip the walkthrough entirely.'
    },
    {
      question: 'Can I negotiate the offer?',
      answer: 'Absolutely. If you feel the offer is low, we\'re open to discussing it. We might be able to adjust based on new information or a follow-up walkthrough. But remember, there\'s no obligation to accept anything you\'re not happy with.'
    },
    {
      question: 'How long does the whole process take?',
      answer: 'From initial offer to closing can be as fast as 7 days or as long as you need. Typically, most sales close within 2-4 weeks from the time an offer is accepted. We work around your schedule.'
    },
    {
      question: 'Who handles the paperwork and title work?',
      answer: 'We coordinate with a licensed title company to handle all paperwork, title search, and closing documents. They manage the official closing. We handle the communication and logistics on our end.'
    },
    {
      question: 'Will I have to meet with a lawyer?',
      answer: 'Most closings don\'t require a personal lawyer, though you\'re welcome to have one review documents if you\'d like. The title company and our team will handle all the legal paperwork. We\'ll explain everything clearly.'
    }
  ];

  const trustFAQ: FAQItem[] = [
    {
      question: 'Are you a real estate agent?',
      answer: 'No. We\'re a direct home buyer. We don\'t list homes or work on commission. We buy properties directly for cash, which is why we can offer a simpler, faster process than traditional real estate agents.'
    },
    {
      question: 'Are you a legitimate company?',
      answer: 'Yes. We\'re a registered business operating in accordance with all local and state laws. We work with licensed title companies, and everything is done through proper legal channels. You can verify our legitimacy and see our track record.'
    },
    {
      question: 'Are there hidden fees?',
      answer: 'No hidden fees. We cover all closing costs—title search, recording fees, title insurance, and any other transaction costs. The offer we make is what you get.'
    },
    {
      question: 'What if I change my mind after accepting an offer?',
      answer: 'Before closing, you have options. If something changes and you need to back out, we can discuss it. However, once you\'ve signed the purchase agreement and we\'ve started title work, backing out could result in legal consequences. That\'s why it\'s important to only move forward when you\'re ready.'
    },
    {
      question: 'How is my personal information protected?',
      answer: 'Your information is kept completely confidential. We don\'t share it without your permission, and we don\'t use it for anything other than evaluating your property. Your privacy matters to us.'
    },
    {
      question: 'What if I have more questions?',
      answer: 'Great! Reach out to us directly. Call us at 412-284-6681 or use the contact form on our site. We\'re happy to answer any questions before you decide to move forward. There\'s no such thing as a silly question.'
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="pt-12 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Find answers to common questions about selling your home to Quick Close.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">General Questions</h2>
          <FAQAccordion items={generalFAQ} />
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Complex Situations</h2>
          <p className="text-gray-600 mb-8">
            Have a complex situation? We help with all of these regularly.
          </p>
          <FAQAccordion items={complexFAQ} />
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Process</h2>
          <FAQAccordion items={processFAQ} />
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Trust & Legitimacy</h2>
          <FAQAccordion items={trustFAQ} />
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Still Have Questions?</h2>
          <p className="text-xl text-gray-600 mb-8">
            We're here to help. Feel free to reach out directly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CTAButton text="Get Your Offer" size="lg" variant="primary" />
            <a
              href="tel:412-284-6681"
              className="inline-flex items-center justify-center bg-gray-600 text-white px-8 py-3 rounded-lg hover:bg-gray-700 transition-colors font-semibold"
            >
              Call: 412-284-6681
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
